import { cloneDeep, isEqual } from 'lodash'
import { DeckResponse } from 'services/apiTypes/deck.types'

import { CardType } from 'types/deck'
import { responseToDeckCards } from 'utils/ResponseToResults'

export type QueryParams = {
  one?: string
  two?: string
  diffFields?: string
  ignoreSideboard?: string
  includeMaybeboards?: string
  limitedCardType?: string
}

export type Diffable = {
  records: Array<CardType>

  totalQuantity: number
  totalEditions: Array<string>
  totalFinishes: Array<string>
  totalCustomCmc: Array<string>
  totalCompanion: Array<string>
  totalColorLabel: Array<string>
  totalCategories: Array<string>
}

export type DiffableFields = Partial<keyof Diffable>
export type Diff = { aDiff: Diffable; bDiff: Diffable; differentFields: DiffableFields[] }

const defaultDiffable: Diffable = {
  records: [],
  totalQuantity: 0,
  totalEditions: [],
  totalFinishes: [],
  totalCustomCmc: [],
  totalCompanion: [],
  totalColorLabel: [],
  totalCategories: [],
}

export const defaultDiffableFields: DiffableFields[] = [
  'totalQuantity',
  'totalEditions',
  'totalFinishes',
  'totalCustomCmc',
  'totalCompanion',
  'totalColorLabel',
  'totalCategories',
]

export function makeCardMaps(a: DeckResponse, b: DeckResponse) {
  const deckACards = responseToDeckCards(a.cards)
  const deckBCards = responseToDeckCards(b.cards)

  const deckACardMap = deckACards.reduce((acc, card) => ({ ...acc, [card.id]: card }), {} as Record<string, CardType>)
  const deckBCardMap = deckBCards.reduce((acc, card) => ({ ...acc, [card.id]: card }), {} as Record<string, CardType>)

  return { deckACardMap, deckBCardMap }
}

export function diffDecks(
  deckA: CardType[],
  deckB: CardType[],
  feildsToDiff: Array<DiffableFields> = defaultDiffableFields,
): [Array<Diffable>, Array<Diffable>, Array<Diff>, Array<Diff>] {
  const aNotB = new Array<Diffable>()
  const bNotA = new Array<Diffable>()
  const same = new Array<Diff>()
  const different = new Array<Diff>()

  const diffableA = mapToDiffable(deckA.sort(orderCardsForDiffing))
  const diffableB = mapToDiffable(deckB.sort(orderCardsForDiffing))

  for (const oracleCardId in diffableA) {
    const aDiff = diffableA[oracleCardId]
    const bDiff = diffableB[oracleCardId]

    if (!bDiff) aNotB.push(aDiff)
  }

  for (const oracleCardId in diffableB) {
    const aDiff = diffableA[oracleCardId]
    const bDiff = diffableB[oracleCardId]

    if (!aDiff) {
      bNotA.push(bDiff)

      continue
    }

    const differentFields = new Array<DiffableFields>()

    for (const diffField of feildsToDiff) {
      if (diffField === 'records') continue
      if (!Object.keys(defaultDiffable).includes(diffField)) continue

      const valueA = aDiff[diffField]
      const valueB = bDiff[diffField]

      if (!Array.isArray(valueA) || !Array.isArray(valueB)) {
        if (valueA !== valueB) differentFields.push(diffField)

        continue
      }

      const sameArrayLength = valueA.length === valueB.length

      if (sameArrayLength && !isEqual(valueA, valueB)) differentFields.push(diffField)
      if (!sameArrayLength && !isEqual(new Set(valueA), new Set(valueB))) differentFields.push(diffField)
    }

    const diffRecord: Diff = { aDiff, bDiff, differentFields }

    if (differentFields.length) different.push(diffRecord)
    else same.push({ aDiff, bDiff, differentFields: [] })
  }

  return [aNotB, bNotA, same, different]
}

function mapToDiffable(deck: CardType[]): Record<string, Diffable> {
  const diffable: Record<string, Diffable> = {}

  for (const card of deck) {
    if (!card.cardId) continue

    if (!diffable[card.oracleCardId]) diffable[card.oracleCardId] = cloneDeep(defaultDiffable)

    diffable[card.oracleCardId].records.push(card)
    diffable[card.oracleCardId].totalQuantity += card.qty
    diffable[card.oracleCardId].totalEditions.push(card.cardId)
    diffable[card.oracleCardId].totalFinishes.push(card.modifier)
    diffable[card.oracleCardId].totalCustomCmc.push(`${card.customCmc ?? null}`)
    diffable[card.oracleCardId].totalCompanion.push(`${card.companion ?? false}`)
    diffable[card.oracleCardId].totalColorLabel.push(JSON.stringify(card.colorLabel))
    diffable[card.oracleCardId].totalCategories.push(...card.categories)
  }

  return diffable
}

function orderCardsForDiffing(a: CardType, b: CardType) {
  if (!a.cardId && !b.cardId) return -1
  if (!a.cardId) return -1
  if (!b.cardId) return 1

  if (a.oracleCardId === b.oracleCardId) return a.cardId > b.cardId ? -1 : 1
  return a.oracleCardId > b.oracleCardId ? -1 : 1
}

export function getDiffPercentages(
  aNotB: Diffable[],
  bNotA: Diffable[],
  same: Diff[],
  different: Diff[],
): [number, number, number] {
  let totalMissPercentage = [...aNotB, ...bNotA].reduce((acc, diff) => (acc += diff.totalQuantity), 0)

  let exactSamePercentage = same.reduce((acc, diff) => {
    acc += diff.aDiff.totalQuantity
    acc += diff.bDiff.totalQuantity

    return acc
  }, 0)

  let slightDiffPercentage = different.reduce((acc, diff) => {
    acc += diff.aDiff.totalQuantity
    acc += diff.bDiff.totalQuantity

    return acc
  }, 0)

  let totalRecords = exactSamePercentage + totalMissPercentage + slightDiffPercentage

  exactSamePercentage = exactSamePercentage / totalRecords
  slightDiffPercentage = slightDiffPercentage / totalRecords
  totalMissPercentage = totalMissPercentage / totalRecords

  return [exactSamePercentage, slightDiffPercentage, totalMissPercentage]
}

export function sortDiffableByCardName(a: Diffable, b: Diffable) {
  const aName = a.records[0].name
  const bName = b.records[0].name

  if (aName === bName) return 0

  return aName > bName ? 1 : -1
}
